/**
 * Text Node Renderer
 *
 * Renders simple text nodes with LOD support:
 * - full: Full text content
 * - title: Truncated text
 * - icon: Text icon (Aa)
 * - dot: Simple dot
 *
 * Text nodes are typically transparent/minimal styling.
 * Phase 0.7: Supports WebGL texture caching for distant nodes
 */

import type { UINode, ScreenMetrics, WorldRect } from '../types';
import type { NodeRenderer, RenderLayers, RenderResult, LODLevel } from '../renderer';
import type { MeasureOptions } from '../content-measure';
import { calculateLOD } from '../renderer';
import { hashContent } from '../texture-cache';

/** Default texture threshold in screen pixels */
const DEFAULT_TEXTURE_THRESHOLD = 60;

/**
 * Text renderer for simple text content.
 */
export class TextRenderer implements NodeRenderer {
	private lastLOD: Map<string, LODLevel> = new Map();
	private lastContent: Map<string, string> = new Map();
	private contentHashes: Map<string, string> = new Map();

	render(node: UINode, metrics: ScreenMetrics, layers: RenderLayers): RenderResult {
		const screenSize = Math.max(metrics.width, metrics.height);
		const opts = layers.options;

		// 1. Calculate LOD first - this is independent of texture state
		// forceLod takes precedence over everything
		const lod: LODLevel = opts.forceLod
			? opts.forceLod
			: opts.simplifyLOD
				? calculateLOD(metrics, {
						enableLodFull: opts.enableLodFull,
						enableLodTitle: opts.enableLodTitle,
						enableLodIcon: opts.enableLodIcon,
						enableLodDot: opts.enableLodDot
					})
				: 'full';

		// 2. Calculate content hash for texture cache invalidation
		// Include LOD level in the hash so different LODs get different textures
		const baseContentHash = this.getContentHash(node);
		const contentHash = `${baseContentHash}:${lod}`;

		// 3. Compound texture ID includes LOD level
		const textureId = `${node.id}:${lod}`;

		// 4. Determine if we should use texture rendering
		// forceTextureRendering forces texture mode regardless of screen size
		const textureThreshold = opts.textureThreshold ?? DEFAULT_TEXTURE_THRESHOLD;
		const wantTexture = (opts.forceTextureRendering || screenSize < textureThreshold) && layers.webgl !== null;

		// 5. Check if we have a valid cached texture for this LOD level
		const hasValidTexture = wantTexture && layers.webgl?.hasValidTexture(textureId, contentHash);

		const lastLod = this.lastLOD.get(node.id);
		const lodChanged = lastLod !== lod;

		// Check if this is a newly created element (e.g., re-entered frustum)
		const isNewElement = layers.html.isNewlyCreated(node.id);

		// Update LOD data attribute
		layers.html.setDataAttribute(node.id, 'lod', lod);

		// Try texture rendering if we have a valid texture for this LOD level
		if (hasValidTexture && layers.webgl) {
			const worldRect: WorldRect = {
				x: node.position.x,
				y: node.position.y,
				width: node.size.width,
				height: node.size.height
			};

			// Rotation is applied via shader (texture is captured without rotation for efficiency)
			const textureDrawn = layers.webgl.drawCachedTexture(textureId, worldRect, 1, node.rotation ?? 0);

			if (textureDrawn) {
				// Successfully drew texture - hide HTML content
				layers.html.setContent(node.id, '');
				layers.html.addClass(node.id, 'texture-cached');

				// Clear lastContent so content is re-rendered when switching back to HTML
				this.lastContent.delete(node.id);

				this.lastLOD.set(node.id, lod);
				return { lod, changed: lodChanged };
			}
		}

		// Fall back to HTML rendering
		layers.html.removeClass(node.id, 'texture-cached');

		// Render content based on LOD (this happens FIRST, before texture capture)
		const content = this.renderContent(node, lod);
		const lastContentValue = this.lastContent.get(node.id);
		const contentChanged = lastContentValue !== content || isNewElement;

		if (contentChanged) {
			layers.html.setContent(node.id, content);
			this.lastContent.set(node.id, content);
		}

		// Request texture generation AFTER HTML content is rendered
		// Pass expectedLOD to guard against race condition where LOD changes during async capture
		if (wantTexture && layers.webgl && !hasValidTexture) {
			layers.webgl.requestTexture(
				textureId,
				() => layers.html.getContainer(node.id),
				contentHash,
				1,
				lod // expectedLOD for race condition validation
			);
		}

		this.lastLOD.set(node.id, lod);

		return { lod, changed: lodChanged || contentChanged };
	}

	/**
	 * Get a content hash for cache invalidation.
	 */
	private getContentHash(node: UINode): string {
		const cached = this.contentHashes.get(node.id);
		if (cached) {
			return cached;
		}

		const contentStr = JSON.stringify(node.data);
		const hash = hashContent(contentStr);
		this.contentHashes.set(node.id, hash);
		return hash;
	}

	private renderContent(node: UINode, lod: LODLevel): string {
		const text = this.getText(node);

		switch (lod) {
			case 'full':
				return `
					<div class="text-node text-full">
						<div class="node-close-btn" data-action="close">&times;</div>
						<div class="text-content">${this.escapeHtml(text)}</div>
					</div>
				`;

			case 'title':
				const truncated = text.length > 30 ? text.slice(0, 30) + '...' : text;
				return `
					<div class="text-node text-title-only">
						<div class="text-truncated">${this.escapeHtml(truncated)}</div>
					</div>
				`;

			case 'icon':
				return `
					<div class="text-node text-icon-only">
						<span class="text-icon">Aa</span>
					</div>
				`;

			case 'dot':
				return `<div class="text-node text-dot"></div>`;
		}
	}

	private getText(node: UINode): string {
		return (
			(node.data.text as string) ||
			(node.data.content as string) ||
			(node.data.label as string) ||
			''
		);
	}

	private escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	getContentHTML(node: UINode): string {
		return this.renderContent(node, 'full');
	}

	getMeasureOptions(node: UINode): MeasureOptions {
		const text = this.getText(node);
		return {
			maxWidth: 400,
			minWidth: 80,
			minHeight: 30,
			contentLength: text.length,
			preferPortrait: true,
		};
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 150,
			height: node.size?.height ?? 60
		};
	}

	cleanup(nodeId: string): void {
		this.lastLOD.delete(nodeId);
		this.lastContent.delete(nodeId);
		this.contentHashes.delete(nodeId);
	}
}
